import { ComparisonItem } from '@upright/ui/common/upright/model/output/scores/comparison/types';
import { compact, sortBy } from 'lodash';
import { pickDefaultModelResult } from '../../../../common/model/data-point-pickers';
import { ModelEntityWithScores } from '../../../../common/model/output/scores/model-entity-data/types';
import {
  RenderFileType,
  RenderItem,
  RenderOptions,
  RenderViewComponentParams,
  RenderViewComponentType,
} from '../../../../common/renderer/types';
import { seriesColorScale } from '../../../common/generic/global/colors';
import { ImpactChartDataSeries } from '../../../common/upright/model/output/scores/net-impact-chart/types';

export function toChartData(items: RenderItem[], entities: ModelEntityWithScores[]): ImpactChartDataSeries[] {
  const dataSeries = items.map((item, index): ImpactChartDataSeries | undefined => {
    const entity = entities.find(e => e.id === item.id && e.type === item.type);
    const modelResult = getModelResultOfRenderItem(item, entity);
    if (!modelResult || !entity) {
      return undefined;
    }
    return {
      impactScoresByTIVV: modelResult.impactScoresByTIVV,
      impactIntervalsByTIVV: modelResult.impactIntervalsByTIVV,
      name: item.name || entity.name,
      id: entity.id,
      resultId: modelResult.resultId,
      type: entity.type,
      color: getColor(item, items.length, index),
      revenue: modelResult.revenue,
    };
  });
  return compact(dataSeries);
}

function getModelResultOfRenderItem(item: RenderItem, entity: ModelEntityWithScores | undefined) {
  if (!entity) {
    return undefined;
  }
  const modelResult =
    entity.modelResults.find(result => result.resultId === item.resultId) ||
    pickDefaultModelResult(entity.modelResults);
  return modelResult;
}

function getColor(item: RenderItem, itemCount: number, index: number) {
  if (itemCount === 1) {
    return undefined;
  }
  if (item.color) {
    return item.color;
  }
  return seriesColorScale(index);
}

export function friendlyRenderType(renderType: RenderFileType) {
  switch (renderType) {
    case RenderFileType.PDF:
      return 'pdf';
    case RenderFileType.PNG:
      return 'png';
  }
}

export function toComparisonItem(renderItem: RenderItem, data: ModelEntityWithScores[]): ComparisonItem {
  const entityData = data.find(e => e.id === renderItem.id && e.type === renderItem.type);
  const result = getModelResultOfRenderItem(renderItem, entityData);

  // OH: Hardcode values to keys that don't exist in renderItem to keep typecheck happy as this feature is no longer accessible anywhere
  // TODO: Either deprecate the whole feature by deleting all code or provide items from comparison context instead of render view context
  const ret: ComparisonItem = {
    id: renderItem.id,
    type: renderItem.type,
    name: renderItem.name,
    resultId: renderItem.resultId,
    color: renderItem.color,
    explicitlySelectedTimeVersion: false,
    hidden: false,
    order: renderItem.index,
    modelResults: [],
    result,
  };
  return ret;
}

const nonTeamComponents = [
  RenderViewComponentType.IMPACT_CHART,
  RenderViewComponentType.LENS_NET_IMPACT,
  RenderViewComponentType.SDG_ALIGNMENTS,
  RenderViewComponentType.CSRD_MATERIALITY_MATRIX,
];

export function getComponentsToRender(
  options: Partial<RenderOptions> | undefined,
  isTeam: boolean,
): RenderViewComponentParams[] {
  const componentParams = options?.componentParams || [];
  const filteredComponents = componentParams.filter(c => isTeam || nonTeamComponents.includes(c.componentType));
  if (!filteredComponents.length) throw Error(`No components to render`);
  const sortedComponentParams = sortBy(filteredComponents, c => c.order);
  return sortedComponentParams;
}

/**
 * This is an old function to support earlier data fetching pattern, where RenderView had centralized
 * score fetching. Now that we can require different kinds of data for different components, each
 * component should handle their own data fetching instead.
 *
 * TODO: Migrate old render view components to handle their own data fetching
 *
 * @deprecated
 */
export function shouldFetchData(options: Partial<RenderOptions>) {
  if (options.items?.length) {
    return true;
  }
  return false;
}

// Only for those components that require the item in their params.
export function getRenderViewItemNameMaybe(params: { item: RenderItem }) {
  return params.item.name || '';
}
